// Pure helpers for the mobile Partners view. No React, no I/O — keep it that
// way so these stay trivially testable.

// Same colours as the desktop LeadStatusChip for each status
// (ChannelPartner.jsx CPID column).
const CHIP_COLOURS = {
  pending:          { color: "#B54708", backgroundColor: "#FFFAEB" },
  approved:         { color: "#027A48", backgroundColor: "#ECFDF3" },
  rejected:         { color: "#B42318", backgroundColor: "#FEF3F2" },
  resubmitted:      { color: "#0E7090", backgroundColor: "#ECFDFF" },
  "Profile Update": { color: "#B54708", backgroundColor: "#FFFAEB" },
  disable:          { color: "#B42318", backgroundColor: "#FEF3F2" },
};

// The statuses the desktop FoSID column renders a chip for. "disable" is not
// among them -- desktop shows no chip for a FOS in that state.
const FOS_CHIP_STATUSES = new Set([
  "pending",
  "approved",
  "rejected",
  "resubmitted",
  "Profile Update",
]);

// Whether the desktop FOS grid would show a chip for this row at all.
export function fosHasChip(row) {
  return Boolean(row?.isDisabled) || FOS_CHIP_STATUSES.has(row?.approvalStatus);
}

// Desktop shows the FOS id as "FOSID" + fosDisplayId.
export function fosDisplayLabel(row) {
  return row?.fosDisplayId ? `FOSID${row.fosDisplayId}` : "—";
}

const DISABLED_CHIP = { color: "#B42318", backgroundColor: "#FEF3F2" };
const NEUTRAL_CHIP = { color: "#667085", backgroundColor: "#F2F4F7" };

// Mirrors the desktop precedence at ChannelPartner.jsx:758 — a disabled login
// hides the approval status entirely.
export function statusChipStyle({ status, disabled } = {}) {
  if (disabled) return { label: "Disabled", ...DISABLED_CHIP };
  if (!status) return { label: "—", ...NEUTRAL_CHIP };
  return { label: status, ...(CHIP_COLOURS[status] ?? NEUTRAL_CHIP) };
}

// Left-edge accent for a card, matching the CpCard rows in CP Sourcing (which
// colour their 4px border from the same value as the tier badge). Reading it
// back off statusChipStyle means the accent can never drift from the chip.
export function statusAccentColor({ status, disabled } = {}) {
  return statusChipStyle({ status, disabled }).color;
}

// Date + time, formatted exactly as the desktop Created At / Last Updated
// columns do (ChannelPartner.jsx formatDate).
export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const DATE_PRESETS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

const DAY_MS = 86400000;

// Whole-day bounds in local time, matching startOfDayMs/endOfDayMs in
// ChannelPartner.jsx. Without these a range would run midnight-to-midnight and
// silently exclude everything recorded on the final day.
export function startOfDayMs(value) {
  const d = new Date(Number(value));
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function endOfDayMs(value) {
  const d = new Date(Number(value));
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

// "custom" returns no bounds on purpose: FilterSheet supplies the explicit
// start/end from the date pickers instead.
export function datePresetToRange(preset, nowMs) {
  const days = Number(preset);
  if (!Number.isFinite(days) || days <= 0) return { start: null, end: null };
  // Day-aligned so "Last 7 days" includes all of today and all of the first day.
  return { start: startOfDayMs(nowMs - days * DAY_MS), end: endOfDayMs(nowMs) };
}

export function buildActiveFilterTags({ statuses, types, datePreset } = {}) {
  const tags = [];
  (statuses ?? []).forEach((value) =>
    tags.push({ dimension: "status", value, label: `Status: ${value}` })
  );
  (types ?? []).forEach((value) =>
    tags.push({ dimension: "type", value, label: `Type: ${value}` })
  );
  if (datePreset && datePreset !== "all") {
    const preset = DATE_PRESETS.find((p) => p.value === datePreset);
    if (preset) {
      tags.push({ dimension: "date", value: datePreset, label: `Date: ${preset.label}` });
    }
  }
  return tags;
}
